import { useRef, useState, type ChangeEvent } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2EmptyState,
  LoadingState,
  Icon,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiDelete, apiGet, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  ProcurementAttachment,
  ProcurementAttachmentListResponse,
} from '../../lib/types/procurement.js';

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function sizeLabel(bytes: number): string {
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toLocaleString('fa-IR', { maximumFractionDigits: 1 })} مگابایت`;
  }
  return `${Math.max(1, Math.round(bytes / 1000)).toLocaleString('fa-IR')} کیلوبایت`;
}

function contentTypeIcon(contentType: string): 'invoice' | 'upload' {
  if (contentType === 'application/pdf') return 'invoice';
  return 'upload';
}

export function ProcurementAttachmentsCard(props: {
  readonly base: string;
  readonly targetType: 'request' | 'order';
  readonly targetRef: string;
  readonly readOnly?: boolean;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const subpath = props.targetType === 'request' ? 'requests' : 'orders';
  const path = `${props.base}/api/v1/procurement/${subpath}/${props.targetRef}/attachments`;
  const list = useApiGet<ProcurementAttachmentListResponse>(path, [path, refreshKey]);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);

    const maxBytes = list.status === 'ready' ? list.data.maxBytes : 10_000_000;
    if (file.size > maxBytes) {
      setError(`حجم فایل بیش از ${sizeLabel(maxBytes)} است.`);
      return;
    }

    setBusy(true);
    try {
      await apiPost(path, {
        fileName: file.name,
        contentType: file.type,
        contentBase64: await readAsBase64(file),
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'بارگذاری پیوست ناموفق بود — فقط PDF و تصویر مجاز است.'));
    } finally {
      setBusy(false);
    }
  }

  async function onOpen(attachmentId: string, fileName: string) {
    setError(null);
    try {
      const file = await apiGet<{ contentType: string; contentBase64: string }>(
        `${path}/${attachmentId}`,
      );
      const bytes = Uint8Array.from(atob(file.contentBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setError(bannerMessageFrom(err, 'دریافت پیوست ناموفق بود.'));
    }
  }

  async function onRemove(attachmentId: string) {
    if (!window.confirm('آیا از حذف این پیوست اطمینان دارید؟')) return;
    setError(null);
    try {
      await apiDelete(`${path}/${attachmentId}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'حذف پیوست ناموفق بود.'));
    }
  }

  return (
    <V2Card>
      <V2CardHeader
        title="پیوست‌ها و اسناد فنی / استعلام"
        subtitle="پیش‌فاکتور، استعلام قیمت، مشخصات فنی، قرارداد — PDF یا تصویر"
        actions={
          !props.readOnly ? (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => void onFile(e)}
              />
              <V2Button
                type="button"
                size="sm"
                variant="secondary"
                icon="plus"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                {busy ? 'در حال بارگذاری…' : 'افزودن مدرک پیوست'}
              </V2Button>
            </>
          ) : null
        }
      />

      {error ? (
        <div
          style={{
            margin: '0 16px 12px 16px',
            padding: '10px 14px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
            fontSize: 13,
            color: 'var(--sk-fg)',
          }}
        >
          {error}
        </div>
      ) : null}

      {list.status === 'loading' ? <LoadingState /> : null}

      {list.status === 'ready' && list.data.items.length === 0 ? (
        <V2EmptyState
          icon="upload"
          title="پیوستی ثبت نشده است"
          description="می‌توانید پیش‌فاکتور یا مستندات فنی را بارگذاری کنید."
          compact
        />
      ) : null}

      {list.status === 'ready' && list.data.items.length > 0 ? (
        <div className="sk-table-scroll">
          <table className="sk-table sk-table--compact">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>نام فایل</th>
                <th style={{ width: 140 }}>تاریخ بارگذاری</th>
                <th style={{ width: 120 }}>حجم</th>
                <th style={{ width: 160, textAlign: 'left' }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((item: ProcurementAttachment, idx: number) => (
                <tr key={item.attachmentId}>
                  <td className="sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                    {(idx + 1).toLocaleString('fa-IR')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name={contentTypeIcon(item.contentType)} size="sm" />
                      <span style={{ fontWeight: 500 }}>{item.fileName}</span>
                    </div>
                  </td>
                  <td className="sk-num" style={{ fontSize: 12 }}>
                    {displayJalaliDate(item.createdAt)}
                  </td>
                  <td className="sk-num" style={{ fontSize: 12 }}>
                    {sizeLabel(item.sizeBytes)}
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <V2Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => void onOpen(item.attachmentId, item.fileName)}
                      >
                        دریافت
                      </V2Button>
                      {!props.readOnly ? (
                        <V2Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          style={{ color: 'var(--sk-danger)' }}
                          onClick={() => void onRemove(item.attachmentId)}
                        >
                          حذف
                        </V2Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </V2Card>
  );
}
